'use client'

import { useParams, usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useCatalogBrandsInitialData } from '@/features/catalog-brands/initial-data'

import {
	catalogBrandsApiSlice,
	CatalogBrandsProductsData,
	createCatalogBrandsUrlParams
} from '@/entities/catalog-brands'
import { getRegionCookies } from '@/entities/regions'

export const useBrandSectionProducts = (brandCodeParam?: string) => {
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const params = useParams()
	const search = searchParams.toString()
	const resolvedUrl = `${pathname}${search ? `?${search}` : ''}`
	const regionCookies = getRegionCookies()
	const { initialProductsData, initialResolvedUrl } = useCatalogBrandsInitialData()
	const initialResolvedUrlRef = useRef<string>(initialResolvedUrl)
	const canUseInitialForCurrentRoute = initialResolvedUrlRef.current === resolvedUrl

	const queryPage = searchParams.get('page')
	const querySort = searchParams.get('sort')
	const sort = querySort ? querySort.toString() : `popular`
	const page = queryPage ? Number(queryPage) : 1

	// Используем переданный параметр, если он есть, иначе получаем из URL
	const brandCode = brandCodeParam || (params.brand as string) || ''

	const [fetchMoreData] = catalogBrandsApiSlice.useLazyGetBrandSectionProductsQuery()
	const [moreData, setMoreData] = useState<CatalogBrandsProductsData | null>(null)

	const urlParams = createCatalogBrandsUrlParams({ sort, page })
	const urlParamsKey = JSON.stringify(urlParams)

	const {
		data: initProductData,
		isFetching: isLazyProductFetching,
		isLoading: isLazyProductLoading
	} = catalogBrandsApiSlice.endpoints.getBrandSectionProducts.useQuery(
		{
			brandCode,
			params: urlParams,
			regionCookies
		},
		{ skip: !brandCode }
	)

	const initialProductsSnapshotKey = `${brandCode || ''}-${urlParamsKey}-${initialProductsData?.TOTAL || 0}-${
		initialProductsData?.ITEMS?.length || 0
	}`
	const initialProductsSnapshotRef = useRef('')
	const initialRequestRef = useRef<{ urlParamsKey: string; brandCode: string } | null>(null)

	if (initialProductsSnapshotRef.current !== initialProductsSnapshotKey) {
		initialProductsSnapshotRef.current = initialProductsSnapshotKey
		initialRequestRef.current = {
			urlParamsKey,
			brandCode: brandCode || ''
		}
	}

	const isInitialRequestArgs = Boolean(
		canUseInitialForCurrentRoute &&
			initialRequestRef.current &&
			initialRequestRef.current.urlParamsKey === urlParamsKey &&
			initialRequestRef.current.brandCode === (brandCode || '')
	)

	const hasQueryItems = Boolean(initProductData?.ITEMS?.length)
	const initialTotal = initialProductsData?.TOTAL || 0
	const queryTotal = initProductData?.TOTAL || 0
	const shouldProtectInitialFromDowngrade = Boolean(initialProductsData?.ITEMS?.length && isInitialRequestArgs)
	const shouldUseQueryData = hasQueryItems && (!shouldProtectInitialFromDowngrade || queryTotal >= initialTotal)
	const effectiveProductData = shouldUseQueryData ? initProductData : initialProductsData

	useEffect(() => {
		setMoreData(null)
	}, [page, sort])

	const getMoreProducts = async (page: number) => {
		const urlParams = createCatalogBrandsUrlParams({ sort, page })

		const moreDataRes = await fetchMoreData(
			{
				brandCode,
				params: urlParams,
				regionCookies
			},
			true
		)

		setMoreData(moreDataRes?.data)
	}

	const products = useMemo(
		() => [...(effectiveProductData?.ITEMS || []), ...(moreData?.ITEMS || [])],
		[effectiveProductData, moreData]
	)

	const hasProducts = Boolean(effectiveProductData?.ITEMS?.length)

	return {
		products,
		currentPage: moreData?.CURRENT_PAGE || effectiveProductData?.CURRENT_PAGE || 0,
		lastPage: effectiveProductData?.LAST_PAGE || 0,
		totalProducts: effectiveProductData?.TOTAL || 0,
		loading: (isLazyProductLoading || isLazyProductFetching) && !hasProducts,
		getMoreProducts
	}
}
